/**
 * Ultrasound Machine API Service for GynHub Medical Platform
 * 
 * This service handles all ultrasound machine management operations including
 * CRUD operations, user associations, and machine status updates.
 */

import { BaseAPI } from './base';

// Machine Types
export interface MachineModality {
  US: 'US';
  CT: 'CT';
  MR: 'MR';
  XR: 'XR';
  MG: 'MG';
  NM: 'NM';
  PT: 'PT';
}

export enum MachineStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  MAINTENANCE = 'maintenance',
  ERROR = 'error'
}

export interface UltrasoundMachine {
  id: string;
  machine_id: string;
  machine_name: string;
  manufacturer: string;
  model: string;
  serial_number?: string;
  ae_title: string;
  ip_address: string;
  dicom_port: number;
  supported_modalities: string[];
  max_concurrent_studies: number;
  department: string;
  room_number?: string;
  institution: string;
  status: MachineStatus;
  assigned_users: string[];
  primary_operator?: string;
  last_heartbeat?: string;
  last_maintenance?: string;
  next_maintenance?: string;
  created_at: string;
  updated_at: string;
  configuration?: Record<string, any>;
  metadata?: Record<string, any>;
}

export interface MachineCreateRequest {
  machine_id: string;
  machine_name: string;
  manufacturer: string;
  model: string;
  serial_number?: string;
  ae_title: string;
  ip_address: string;
  dicom_port: number;
  supported_modalities: string[];
  max_concurrent_studies?: number;
  department: string;
  room_number?: string;
  institution: string;
  assigned_users?: string[];
  primary_operator?: string;
  configuration?: Record<string, any>;
  metadata?: Record<string, any>;
}

export interface MachineUpdateRequest {
  machine_name?: string;
  manufacturer?: string;
  model?: string;
  serial_number?: string;
  ae_title?: string;
  ip_address?: string;
  dicom_port?: number;
  supported_modalities?: string[];
  max_concurrent_studies?: number;
  department?: string;
  room_number?: string;
  institution?: string;
  status?: MachineStatus;
  assigned_users?: string[];
  primary_operator?: string;
  configuration?: Record<string, any>;
  metadata?: Record<string, any>;
}

export interface MachineListResponse {
  success: boolean;
  message: string;
  machines: UltrasoundMachine[];
  total_count: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface MachineResponse {
  success: boolean;
  message: string;
  data: UltrasoundMachine;
}

export interface MachineStatusUpdate {
  status: MachineStatus;
  last_heartbeat?: string;
  notes?: string;
}

// Machine statistics response
export interface MachineStatsResponse {
  success: boolean;
  message: string;
  data: {
    total_machines: number;
    active_machines: number;
    machines_by_status: Record<MachineStatus, number>;
    machines_by_department: Record<string, number>;
    machines_by_manufacturer: Record<string, number>;
    recent_activity: any[];
  };
}

// AE Title validation types
export interface AeTitleValidationResult {
  success: boolean;
  message: string;
  data: {
    ae_title: string;
    is_unique: boolean;
    is_valid: boolean;
    existing_machine?: {
      machine_id: string;
      machine_name: string;
      department: string;
    };
    suggestions: string[];
  };
}

class UltrasoundMachineAPI extends BaseAPI {
  private readonly basePath = '/api/v1/ultrasound-machines';

  /**
   * Create a new ultrasound machine (Admin only)
   */
  async createMachine(machineData: MachineCreateRequest): Promise<MachineResponse> {
    try {
      const response = await this.request<MachineResponse>({
        method: 'POST',
        url: this.basePath,
        data: machineData
      });
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to create ultrasound machine');
      }
      
      return response;
    } catch (error: any) {
      console.error('❌ Error creating ultrasound machine:', error);
      throw new Error(error.message || 'Failed to create ultrasound machine');
    }
  }

  /**
   * List ultrasound machines with pagination and filters
   */
  async listMachines(params?: {
    skip?: number;
    limit?: number;
    search?: string;
    department?: string;
    status?: MachineStatus;
    modality?: string;
  }): Promise<MachineListResponse> {
    try {
      const queryParams = new URLSearchParams();
      
      if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString());
      if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());
      if (params?.search) queryParams.append('search', params.search);
      if (params?.department) queryParams.append('department', params.department);
      if (params?.status) queryParams.append('status', params.status);
      if (params?.modality) queryParams.append('modality', params.modality);
      
      const url = queryParams.toString() ? `${this.basePath}?${queryParams}` : this.basePath;
      const response = await this.request<MachineListResponse>({
        method: 'GET',
        url: url
      });
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch ultrasound machines');
      }
      
      return response;
    } catch (error: any) {
      console.error('❌ Error listing ultrasound machines:', error);
      throw new Error(error.message || 'Failed to fetch ultrasound machines');
    }
  }

  /**
   * Get machines assigned to the current user
   */
  async getUserMachines(): Promise<MachineListResponse> {
    try {
      const response = await this.request<MachineListResponse>({
        method: 'GET',
        url: `${this.basePath}/my-machines`
      });
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch assigned machines');
      }
      
      return response;
    } catch (error: any) {
      // Log error but don't throw for 400 errors that might be handled at component level
      if (error.response?.status === 400) {
        console.warn('⚠️ User machines API returned 400 - may be handled at component level:', error.message);
      } else {
        console.error('❌ Error fetching user machines:', error);
      }
      throw new Error(error.message || 'Failed to fetch assigned machines');
    }
  }

  /**
   * Get a specific machine by ID
   */
  async getMachine(machineId: string): Promise<MachineResponse> {
    try {
      const response = await this.request<MachineResponse>({
        method: 'GET',
        url: `${this.basePath}/${machineId}`
      });
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch ultrasound machine');
      }
      
      return response;
    } catch (error: any) {
      console.error(`❌ Error fetching ultrasound machine ${machineId}:`, error);
      throw new Error(error.message || 'Failed to fetch ultrasound machine');
    }
  }

  /**
   * Update a machine's information (Admin only)
   */
  async updateMachine(machineId: string, updateData: MachineUpdateRequest): Promise<MachineResponse> {
    try {
      const response = await this.request<MachineResponse>({
        method: 'PUT',
        url: `${this.basePath}/${machineId}`,
        data: updateData
      });
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to update ultrasound machine');
      }
      
      return response;
    } catch (error: any) {
      console.error(`❌ Error updating ultrasound machine ${machineId}:`, error);
      throw new Error(error.message || 'Failed to update ultrasound machine');
    }
  }

  /**
   * Delete a machine record (Admin only)
   */
  async deleteMachine(machineId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.request<{ success: boolean; message: string }>({
        method: 'DELETE',
        url: `${this.basePath}/${machineId}`
      });
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to delete ultrasound machine');
      }
      
      return response;
    } catch (error: any) {
      console.error(`❌ Error deleting ultrasound machine ${machineId}:`, error);
      throw new Error(error.message || 'Failed to delete ultrasound machine');
    }
  }

  /**
   * Update machine status
   */
  async updateMachineStatus(machineId: string, statusUpdate: MachineStatusUpdate): Promise<MachineResponse> {
    try {
      const response = await this.request<MachineResponse>({
        method: 'PATCH',
        url: `${this.basePath}/${machineId}/status`,
        data: statusUpdate
      });
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to update machine status');
      }
      
      return response;
    } catch (error: any) {
      console.error(`❌ Error updating machine status ${machineId}:`, error);
      throw new Error(error.message || 'Failed to update machine status');
    }
  }

  /**
   * Get machines overview statistics (Admin only)
   */
  async getMachineStats(): Promise<MachineStatsResponse> {
    try {
      const response = await this.request<MachineStatsResponse>({
        method: 'GET',
        url: `${this.basePath}/stats/overview`
      });
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch machine statistics');
      }
      
      return response;
    } catch (error: any) {
      console.error('❌ Error fetching machine statistics:', error);
      throw new Error(error.message || 'Failed to fetch machine statistics');
    }
  }

  /**
   * Validate AE Title uniqueness and format (Admin only)
   */
  async validateAeTitle(aeTitle: string, machineId?: string): Promise<AeTitleValidationResult> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('ae_title', aeTitle);
      if (machineId) {
        queryParams.append('machine_id', machineId);
      }
      
      const response = await this.request<AeTitleValidationResult>({
        method: 'GET',
        url: `${this.basePath}/validate-ae-title?${queryParams.toString()}`
      });
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to validate AE Title');
      }
      
      return response;
    } catch (error: any) {
      console.error('❌ Error validating AE Title:', error);
      throw new Error(error.message || 'Failed to validate AE Title');
    }
  }

  /**
   * Check AE Title availability and get suggestions (Admin only)
   */
  async checkAeTitleAvailability(aeTitle: string): Promise<AeTitleValidationResult> {
    try {
      const response = await this.request<AeTitleValidationResult>({
        method: 'POST',
        url: `${this.basePath}/validate-ae-title`,
        data: { ae_title: aeTitle }
      });
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to check AE Title availability');
      }
      
      return response;
    } catch (error: any) {
      console.error('❌ Error checking AE Title availability:', error);
      throw new Error(error.message || 'Failed to check AE Title availability');
    }
  }
}

// Export singleton instance
export const ultrasoundMachineAPI = new UltrasoundMachineAPI();
